import clsx from 'clsx'
import { useMemo, useEffect, useState } from 'preact/hooks'
import { render } from 'preact'
import Icon from '@/components/snippets/icon/icon'
import { useSwatches } from '@/components/hooks/useSwatches'
import { fetchSiblingProducts } from '@/components/libs/storefront/siblings'
import { getLowQualityPlaceholder, generateSrcSet } from './ResultImage'

const SWATCH_MODAL_ID = 'quick-add-modal'
const IMAGE_WIDTHS = [320, 480, 560, 640, 720, 960]
const SKELETON_TILE_COUNT = 9

function toImageUrl(val) {
  if (val == null) return null
  if (typeof val === 'string' && val.length > 0) return val
  const url = typeof val === 'object' ? val.url ?? val.src : null
  return url != null ? String(url) : null
}

const MARKET_OOS_TAG = window.globals?.localization?.market
  ? `Flow:OutOfStock:${window.globals.localization.market}`
  : null

function isSwatchOutOfStock(swatch) {
  if (swatch.tags?.includes('Flow:OutOfStock')) return true
  if (MARKET_OOS_TAG && swatch.tags?.includes(MARKET_OOS_TAG)) return true
  return false
}

function getSwatchBgColour(swatch) {
  const tag = swatch?.tags?.find((t) => t.includes('Hex'))
  const colour = tag ? tag.split('-').pop() : 'var(--colour-accent)'
  return swatch?.background_colour || colour
}

const SwatchModalContent = ({ handles, currentHandle, onClose }) => {
  const [swatches, setSwatches] = useState([])
  const [loading, setLoading] = useState(true)

  const handlesKey = useMemo(() => (handles ?? []).join(','), [handles])

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    fetchSiblingProducts(handles ?? [])
      .then((data) => {
        if (cancelled) return
        const sorted = [...data].sort((a, b) => {
          const aCurrent = a.handle === currentHandle
          const bCurrent = b.handle === currentHandle
          if (aCurrent === bCurrent) return 0
          return aCurrent ? -1 : 1
        })
        setSwatches(sorted)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setSwatches([])
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [handlesKey])

  const filteredSwatches = useMemo(
    () => swatches.filter((swatch) => !isSwatchOutOfStock(swatch)),
    [swatches]
  )

  useEffect(() => {
    if (window.lazyLoadInstance) {
      setTimeout(() => window.lazyLoadInstance.update(), 0)
    }
  }, [filteredSwatches])

  const count = filteredSwatches.length
  const skeletonCount = Math.min(SKELETON_TILE_COUNT, (handles ?? []).length || SKELETON_TILE_COUNT)
  const visibleCount = loading ? skeletonCount : count
  const gridClass =
    visibleCount < 4 ? 'grid-cols-2' : visibleCount >= 10 ? 'grid-cols-3 md:grid-cols-4' : 'grid-cols-3'

  return (
    <>
      <div className="sticky z-20 flex h-[60px] w-full shrink-0 items-center justify-between gap-x-1 border-b border-neutral-medium bg-secondary px-margin inset-tl md:h-[84px]">
        <h2 className="h4">
          {loading
            ? 'Loading Colours…'
            : `View All ${count} Colour${count === 1 ? '' : 's'}`}
        </h2>
        <button
          className="size-[14px] link-fade children:size-full"
          data-modal-parent={SWATCH_MODAL_ID}
          data-modal-role="close"
          aria-label="Close colours modal"
          onClick={onClose}
        >
          <Icon id="close" />
        </button>
      </div>
      <div className="flex grow flex-col gap-y-margin overflow-y-auto overscroll-none p-margin">
        <p className="paragraph-b text-neutral-secondary">Tap colour to view product</p>
        <div className={clsx('grid gap-0.5 md:gap-0.75', gridClass)}>
          {loading
            ? Array.from({ length: skeletonCount }).map((_, idx) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`skeleton-${idx}`}
                className="overflow-hidden relative aspect-3/4 w-full animate-pulse rounded border border-neutral-light bg-neutral-light"
                aria-hidden="true"
              />
            ))
            : filteredSwatches.map((swatch) => {
              const imageSrc = toImageUrl(swatch.image)
              const title = swatch.colourName || ''
              return (
                <a
                  key={swatch.handle}
                  href={swatch.url}
                  className="group/swatch-card overflow-hidden flex flex-col rounded border border-neutral-light bg-neutral-light"
                  aria-label={title ? `View product in ${title}` : 'View product'}
                  data-dl-track="select_item"
                  data-dl-product-id={swatch.productLegacyResourceId}
                  data-dl-attribute="item_colour"
                  data-dl-value={swatch.colourName}
                >
                  <div
                    className="overflow-hidden relative aspect-3/4 w-full bg-[--bg-colour] children:size-full children:object-cover"
                    style={{ '--bg-colour': getSwatchBgColour(swatch) }}
                  >
                    {imageSrc && (
                      <img
                        className="lazy transition-all group-hover/swatch-card:scale-[1.02]"
                        src={getLowQualityPlaceholder(imageSrc)}
                        data-src={imageSrc}
                        data-srcset={generateSrcSet(imageSrc, IMAGE_WIDTHS)}
                        data-sizes="(max-width: 640px) 50vw, 33vw"
                        alt={title || 'Product'}
                      />
                    )}
                  </div>
                </a>
              )
            })}
        </div>
      </div>
    </>
  )
}

function openSwatchModal({ handles, currentHandle }) {
  const container = document.querySelector('[data-quick-add-modal-inner]')
  if (!container) return
  const modalId = SWATCH_MODAL_ID
  const closeModal = () => {
    const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
    if (modal && typeof modal.handleHide === 'function') {
      modal.handleHide()
    }
  }
  render(
    <SwatchModalContent handles={handles} currentHandle={currentHandle} onClose={closeModal} />,
    container
  )
  setTimeout(() => {
    const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
    if (modal && typeof modal.handleToggle === 'function') {
      modal.handleToggle()
    }
  }, 100)
}

export function ResultsSwatches({ result, swatches }) {
  if (!swatches || swatches.length === 0) return null

  const filteredSwatches = swatches.filter((swatch) => !isSwatchOutOfStock(swatch))
  const siblingHandles = Array.isArray(result?.siblingHandles) ? result.siblingHandles : []

  const totalCount = siblingHandles.length || filteredSwatches.length
  const handlesForModal = siblingHandles.length
    ? siblingHandles
    : filteredSwatches.map((s) => s.handle).filter(Boolean)

  const handleOpenModal = () => {
    openSwatchModal({ handles: handlesForModal, currentHandle: result.handle })
  }

  return (
    <button
      type="button"
      className="flex w-fit flex-wrap items-center gap-x-0.25 gap-y-0.125 link-fade"
      onClick={handleOpenModal}
      aria-label="View all colours"
    >
      {filteredSwatches.slice(0, 3).map((swatch) => (
        <SwatchDot key={swatch.handle} swatch={swatch} isCurrentSwatch={swatch.handle === result.handle} />
      ))}
      {totalCount > 3 && (
        <span className="paragraph-xs text-line text-neutral-secondary">+ More Colours</span>
      )}
    </button>
  )
}

function SwatchDot({ swatch, isCurrentSwatch }) {
  const colour = useMemo(() => {
    const tag = swatch?.tags?.find((tag) => tag.includes('Hex'))
    if (!tag) return 'var(--colour-accent)'
    return tag.split('-').pop()
  }, [swatch])

  const isLightColour = useMemo(() => {
    const hexColour = colour.toLowerCase()
    return !hexColour || hexColour === '#ffffff' || hexColour === '#fff' || hexColour === 'white'
  }, [colour])

  const criteria = useMemo(
    () => ({
      tags: swatch?.tags ?? []
    }),
    [swatch?.tags]
  )

  const altSwatch = useSwatches(criteria)

  return (
    <div
      className={clsx(
        'overflow-hidden flex size-1.375 shrink-0 rounded-full border-[4px] border-secondary bg-[--bg-colour] outline outline-1 outline-offset-0 outline-transparent transition-all',
        isCurrentSwatch ? '!outline-brand-black' : ''
      )}
      style={{
        '--bg-colour': altSwatch.length > 0 && altSwatch[0].background_colour ? altSwatch[0].background_colour : colour,
        ...(isLightColour && { boxShadow: 'inset 0px 0px 0px 1px var(--darkGrey)' })
      }}
    >
      {altSwatch.length > 0 && altSwatch[0].image && (
        <img src={altSwatch[0].image} alt="Product swatch" className="size-full object-cover" />
      )}
    </div>
  )
}
